import axios from 'axios';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://asia-southeast1-catalinx.cloudfunctions.net/weddingFunctions';

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// React Query hooks for data fetching
export const usePhotosInfinite = (pageSize = 50, options = {}) => {
  return useInfiniteQuery({
    queryKey: ['photos', 'infinite', pageSize],
    queryFn: async ({ pageParam = 0 }) => {
      const token = localStorage.getItem('weddingToken');
      const response = await api.get('/photos', {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: pageParam, limit: pageSize },
      });
      return {
        photos: response.data.photos,
        pagination: response.data.pagination,
        nextPage: response.data.pagination.hasMore ? pageParam + 1 : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: 30 * 1000, // 30 seconds - quick refresh for new uploads
    gcTime: 10 * 60 * 1000, // 10 minutes cache - reasonable memory usage
    refetchOnWindowFocus: false,
    ...options,
  });
};

// Keep the old hook for backward compatibility (loads all photos at once)
export const useGuests = (options = {}) => {
  return useQuery({
    queryKey: ['guests'],
    queryFn: async () => {
      const token = localStorage.getItem('weddingToken');
      const response = await api.get('/guests', {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes - guest data changes occasionally
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    ...options,
  });
};

export const useAttendanceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ guestName, tableNumber, attended }) => {
      const token = localStorage.getItem('weddingToken');
      const response = await api.post('/attendance', {
        guestName,
        tableNumber,
        attended,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Optimistically update the guests cache
      queryClient.setQueryData(['guests'], (oldData) => {
        if (!oldData) return oldData;
        return oldData.map(guest => {
          const guestName = guest.name || guest.guest_name || '';
          const tableNumber = guest.table_number || guest.table || '';
          if (guestName.toLowerCase().includes(variables.guestName.toLowerCase()) &&
              tableNumber.toString() === variables.tableNumber.toString()) {
            return { ...guest, attended: variables.attended ? 'Y' : 'N' };
          }
          return guest;
        });
      });
    },
    onError: (error, variables) => {
      // Revert optimistic update on error by refetching
      queryClient.invalidateQueries(['guests']);
    },
  });
};

// Generate upload URL for a file
export const useUploadUrlMutation = () => {
  return useMutation({
    mutationFn: async ({ fileName, contentType }) => {
      const token = localStorage.getItem('weddingToken');
      const response = await api.post('/upload-url', {
        fileName,
        contentType,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });
};

// Upload file to signed URL with progress tracking
export const uploadFile = async (url, file, onProgress, onSuccess) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // Call success callback to invalidate caches
        if (onSuccess) {
          onSuccess();
        }
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });

    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
};

export default api;